import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { Field } from 'src/API';
import { FieldInput } from 'src/api/process-module';
import '../components/markdown-styles.css';

interface CheckboxInputProps {
  props: any;
  onChange?: (checked: boolean, taskId: string) => void;
}

const CheckboxInput: React.FC<CheckboxInputProps> = ({ props, onChange }) => {
  const handleCheckboxClick = (event: React.MouseEvent<HTMLInputElement>) => {
    const checkbox = event.target as HTMLInputElement;
    const taskId = checkbox.getAttribute('data-task-id');

    if (taskId && onChange) {
      onChange(checkbox.checked, taskId);
    }
  };

  return <input {...props} disabled={true} onClick={handleCheckboxClick} data-task-id={props.id} />;
};

// Helper function to parse inline style string to style object
const parseStyleString = (styleString: string | React.CSSProperties): React.CSSProperties => {
  if (typeof styleString !== 'string') {
    return styleString || {};
  }

  const styleObject: React.CSSProperties = {};
  const declarations = styleString.split(';').filter((decl) => decl.trim());

  declarations.forEach((declaration) => {
    const [property, value] = declaration.split(':').map((s) => s.trim());
    if (property && value) {
      // Convert kebab-case to camelCase for React style objects
      const camelCaseProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
      styleObject[camelCaseProperty as any] = value;
    }
  });

  return styleObject;
};

interface MarkdownFieldProps {
  field: FieldInput & Partial<Field>;
  fieldProps?: any;
  value?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
}

const getMarkdownComponents = (onChange?: (value: string) => void) => ({
  h1: ({ children }) => (
    <Typography variant="h1" sx={{ mt: 3, mb: 2 }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="h2" sx={{ mt: 3, mb: 2 }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="h3" sx={{ mt: 2.5, mb: 1.5 }}>
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography variant="h4" sx={{ mt: 2, mb: 1 }}>
      {children}
    </Typography>
  ),
  h5: ({ children }) => (
    <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
      {children}
    </Typography>
  ),
  h6: ({ children }) => (
    <Typography variant="h6" sx={{ mt: 1.5, mb: 1 }}>
      {children}
    </Typography>
  ),
  p: ({ style, children, className, ...props }: any) => {
    if (style) {
      const parsedStyle = parseStyleString(style);
      return (
        <Typography paragraph style={parsedStyle} className={className} {...props}>
          {children}
        </Typography>
      );
    }
    return (
      <Typography paragraph className={className} {...props}>
        {children}
      </Typography>
    );
  },
  a: ({ href, children, ...props }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: '#1976d2',
        textDecoration: 'underline',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
      }}
      {...props}
    >
      {children}
      <OpenInNewIcon
        style={{
          fontSize: '14px',
          verticalAlign: 'middle',
          opacity: 0.7,
        }}
      />
    </a>
  ),
  img: ({ src, alt, ...props }) => (
    <Box
      sx={{
        my: 2,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt || 'Image'}
        {...props}
        sx={{
          maxWidth: '100%',
          height: 'auto',
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}
        onError={(e: any) => {
          e.target.style.display = 'none';
          e.target.parentElement.innerHTML = `<div style="padding: 16px; background-color: #f5f5f5; border-radius: 8px; text-align: center; color: #666;">Failed to load image: ${alt || src}</div>`;
        }}
      />
    </Box>
  ),
  code: ({ inline, className, children, ...props }: any) => {
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : '';

    return !inline && language ? (
      <Box sx={{ my: 2 }}>
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={language}
          PreTag="div"
          customStyle={{
            borderRadius: '8px',
            padding: '16px',
          }}
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      </Box>
    ) : (
      <code
        className={className}
        style={{
          backgroundColor: '#f5f5f5',
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '0.9em',
          fontFamily: 'monospace',
        }}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ children }) => <>{children}</>,
  blockquote: ({ children }) => (
    <Box
      sx={{
        borderLeft: '4px solid #1976d2',
        pl: 2,
        py: 1,
        my: 2,
        backgroundColor: '#f8fafc',
        borderRadius: '0 8px 8px 0',
      }}
    >
      {children}
    </Box>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ pl: 3, my: 1 }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ pl: 3, my: 1 }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" sx={{ mb: 0.5 }}>
      {children}
    </Typography>
  ),
  hr: () => (
    <Box
      component="hr"
      sx={{
        my: 3,
        border: 'none',
        borderTop: '2px solid #e0e0e0',
      }}
    />
  ),
  table: ({ children }) => (
    <TableContainer component={Paper} sx={{ my: 2, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
      <Table>{children}</Table>
    </TableContainer>
  ),
  thead: ({ children }) => <TableHead>{children}</TableHead>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => <TableRow>{children}</TableRow>,
  th: ({ children }) => <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{children}</TableCell>,
  td: ({ children }) => <TableCell>{children}</TableCell>,
  input: (props) => {
    if (props.type === 'checkbox') {
      return (
        <CheckboxInput
          props={props}
          onChange={(checked, taskId) => {
            if (onChange) {
              onChange(`[${checked ? 'x' : ' '}] ${taskId}`);
            }
          }}
        />
      );
    }
    return <input {...props} />;
  },
  // Support for colored text via HTML with proper style parsing
  span: ({ children, className, ...props }: any) => {
    const style = props.node?.properties?.style;
    const parsedStyle = style ? parseStyleString(style) : {};
    return (
      <span style={parsedStyle} className={className}>
        {children}
      </span>
    );
  },
  div: ({ children, className, ...props }: any) => {
    const style = props.node?.properties?.style;
    const parsedStyle = style ? parseStyleString(style) : {};
    return (
      <div style={parsedStyle} className={className}>
        {children}
      </div>
    );
  },
  // Support for other HTML elements that might have styles
  strong: ({ children, ...props }: any) => {
    const style = props.node?.properties?.style;
    const parsedStyle = style ? parseStyleString(style) : {};
    return <strong style={parsedStyle}>{children}</strong>;
  },
  em: ({ children, ...props }: any) => {
    const style = props.node?.properties?.style;
    const parsedStyle = style ? parseStyleString(style) : {};
    return <em style={parsedStyle}>{children}</em>;
  },
});

export const MarkdownField: React.FC<MarkdownFieldProps> = ({ field, fieldProps, value, disabled, onChange }) => {
  if (field.properties?.disabled) {
    return null;
  }

  // Use the value prop if provided (for real-time updates), otherwise fall back to field data
  const markdownContent = (() => {
    if (value !== undefined && value !== null) {
      return String(value);
    }
    const source = (field as any).properties?.dataSource || field.dataSource;
    return source ? String(source) : '';
  })();

  return (
    <Box
      sx={{
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        maxWidth: '100%',
        '& *': {
          wordWrap: 'break-word',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          maxWidth: '100%',
        },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        skipHtml={false}
        components={getMarkdownComponents(onChange)}
      >
        {markdownContent}
      </ReactMarkdown>
    </Box>
  );
};
